import type { Column, SortDirection } from '../claim-history/simple-table'

import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import Parser from 'html-react-parser'
import _ from '../../utils/lodash-optimized'
import React, { useState } from 'react'
import { BsExclamationTriangleFill } from 'react-icons/bs'
import { MdContentPasteGo } from 'react-icons/md'
import { RiFolderTransferLine } from 'react-icons/ri'
import { Link, useNavigate } from 'react-router-dom'
// @ts-expect-error // it's okay
import statuses from 'shared_src/dictionaries/episodeOfCare/statuses'

import http from '../../lib/http'
import { useAppDispatch, useAppSelector } from '../../store'
import { selectTab } from '../../store/modules'
import { ChatType } from '../chats'
import * as Table from '../claim-history/simple-table'
import * as EncounterActions from '../Encounter/actions'
import { Domain } from '../igao/shared'
import { diffDate, igaoPages } from '../igao/utils'
import { useSidebar } from '../layout/context'
import { DOMAIN_LIST } from '../layout/sidebar/tasks'
import { SidebarOptionKind } from '../layout/sidebar/types'
import * as Card from '../shared/card'
import { ContextMenuButton } from '../shared/ContextMenu'

import styles from './tasks.module.css'

interface Task extends Record<string, unknown> {
  id: string
  description: string
  actions: Array<{
    description: string
    status: string
    dueDate: string
    urgent: boolean
  }>
  domain: string
  dueDate?: string
  firstName: string
  lastName: string
  issueAuthorId: string
  itemId: string
  path: string
  patientId: string
  patientStatus: string
  status: string
  urgent: boolean
  client: string
  clientName: string

  created_at: string
}

interface Response {
  tabs: {
    hold_actions: number
    most_outdated: string
    my_issues: number
    new_actions: number
    open_actions: number
    urgents: number
  }
  filters: Array<{
    key: string
    options: string[]
  }>
  data: Task[]
}

interface FilterItem {
  name: string
  options: Array<{
    value: string | number | boolean
    label?: string
    helperText?: string
  }>
}

const statusMapping: Record<string, unknown> = {
  'in-progress': 'Open',
  'on-hold': 'Hold',
  'deferred': 'Deferred',
  'not-started': 'New'
}

const pathToURLMap: Record<string, string> = {
  patient: 'standarddemographicspatient',
  insurances: 'standarddemographicsinsurance',
  home_assessment: 'standarddemographicshomeassessment',
  family_support: 'standarddemographicssupport',
  provider_network: 'standarddemographicsmedical',
  diagnosis: 'standardmedicaldiagnosis',
  admissions: 'standardmedicalevents',
  maternity: 'standardmaternity',
  lifestyle: 'standardsocialhistorylifestyle',
  resources: 'standardsocialhistoryresources',
  substance_use: 'standardsocialhistorysubstance',
  medications: 'standardmedications',
  esas: 'standardsymptomsesas',
  review_of_systems: 'standardsymptomsros',
  vitals: 'standardvitals',
  general_documents: 'standarddocuments',
  goals: 'standardgoals'
}

export function Tasks (): JSX.Element | null {
  const navigate = useNavigate()
  const sidebar = useSidebar()
  const dispatch = useAppDispatch()

    const globalVars = useAppSelector((state) => state.globalVars)
    const clients = globalVars?.clients
    const auth = useAppSelector((state) => state.auth)

  const [ sort, setSort ] = useState<{ dir: SortDirection, field: keyof Task}>({ dir: 'asc', field: 'due_date' })
  const [ currentFilters, setCurrentFilters ] = useState<Record<keyof Task, Array<string | number | boolean>>>({})
  const [ pagination, setPagination ] = useState<{ page: number, size: number }>({ page: 0, size: 10 })
  const [ filters ] = useState<FilterItem[]>([])

  const query = useQuery({
    queryKey: [ 'tasks', sort.dir, sort.field, pagination.page, pagination.size, currentFilters ],
    queryFn: async () => {
      const data = await http.getRequest<any>('/tasks', {
        sortBy: sort.field,
        sortDir: sort.dir,
        page: pagination.page,
        size: pagination.size,
        status: currentFilters.status ?? [],
        domain: currentFilters.domain ?? [],
        patient_status: currentFilters.patient_status ?? [],
        scope: 'mine'
      })

      return data
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })

  function handleFilterChange (key: keyof Task, value: Array<string | number | boolean>): void {
    setCurrentFilters((prev) => ({ ...prev, [key]: value }))
  }

  function handleStartNewEncounter (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, patientId: string, client: string): void {
    e.stopPropagation()

    let redirectTo = 'igao'
      const role = auth?.profile?.app_metadata?.roles?.[`${client}-compassion`]
    if (role === 'administrative_support') {
      redirectTo = 'standarddemographicspatient'
    }

    dispatch(
      EncounterActions.encounterStart(
        patientId,
        auth.profile,
        'patient',
        {},
        redirectTo,
        navigate,
        () => {
          if (!sidebar.state || sidebar.state === 'closed') {
            setTimeout(() => {
              sidebar.setState(SidebarOptionKind.messages)
              dispatch(selectTab(ChatType.Current))
            }, 500)
          }
        },
        client
      )
    )
  }

  const columns: Array<Column<Task>> = [
    {
      name: 'action_description',
      header: 'Action',
      gridArea: '2.3fr',
      isSortable: true,
      getValue: ({ description }) => (
        <div style={{ lineHeight: '21px', letterSpacing: '0.1px', marginTop: '4px' }}>{description}</div>
      )
    },
    {
      name: 'due_date',
      header: 'Due Days',
      isSortable: true,
      style: { textAlign: 'center' },
      headerStyle: { justifyContent: 'center' },
      getValue: ({ dueDate, urgent }) => {
        const days = diffDate(dueDate)

        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '3px' }}>

            {urgent && (
              <BsExclamationTriangleFill
                fill='#F2545A'
                size='1.2rem'
              />
            )}

            {typeof days === 'number' && !isNaN(days) && (
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  background: days >= 0 ? '#62c5e0' : '#f9797c',
                  color: 'white',

                  fontSize: '10px',
                  fontWeight: '500',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '50%'
                }}
              >
                {Math.abs(Math.ceil(days))}
              </div>
            )}
          </div>
        )
      }
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true,
      // @ts-expect-error // it's okay
      getValue: ({ status }) => <div style={{ marginTop: '4px' }}>{statusMapping[status] ?? status}</div>
    },
    {
      name: 'last_name',
      header: 'Last Name',
      gridArea: '0.9fr',
      isSortable: true,
      getValue: ({ lastName, client, patientId }) => (
        <div style={{ marginTop: '4px' }}>
          <Link
            to={`/patient/${client}/${patientId}/patientFolder`}
            onClick={(e) => e.stopPropagation()}
          >
            {lastName}
          </Link>
        </div>
      )
    },
    {
      name: 'first_name',
      header: 'First Name',
      getValue: ({ firstName }) => (
        <div style={{ marginTop: '4px' }}>{firstName}</div>
      ),
      gridArea: '1.1fr',
      isSortable: true
    },
    {
      name: 'patient_status',
      header: 'Patient Status',
      getValue: (item) => item.patient_status
        ? _.find(statuses, (x: { value: unknown }) => x.value === item.patient_status).label
        : 'Unknown',
      isSortable: true
    },
    {
      name: 'domain',
      header: 'Domain',
      isSortable: true,
      gridArea: '0.8fr',
      style: { textAlign: 'center' },
      headerStyle: { justifyContent: 'center' },
      getValue: ({ domain }) => {
        const domainId = DOMAIN_LIST.find((domainl) => domainl.value === domain)?.id
        return (
          <div style={{ display: 'flex', justifyContent: 'center', transform: 'scale(0.8)' }}>
            <Domain domain={domainId} />
          </div>
        )
      }
    },
    {
      name: 'issue_description',
      header: 'Issue',
      gridArea: '2.3fr',
      isSortable: true,
      getValue: ({ description }) => (
        <div style={{ lineHeight: '21px', letterSpacing: '0.1px' }}>{Parser(description)}</div>
      )
    },
    {
      name: 'client_name',
      header: 'Client',
      isSortable: true,
      getValue: ({ clientName, client }) => {
        return (
          <div style={{ marginTop: '4px' }}>{clients?.find((c) => c.clientName === client)?.display ?? clientName}</div>
        )
      }
    },
    {
      name: 'actions',
      header: null,
      getValue: ({
        // have_encounter_in_progress: haveEncounterInProgress,
        patientId,
        client,
        path,
        itemId
      }) => {
        const urlFromPath = path ? pathToURLMap[path] : null

        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
            <ContextMenuButton
              actions={[
                {
                  id: 'continue-encounter',
                  icon: RiFolderTransferLine,
                  label: 'Start new Patient Encounter',
                  onClick: (e) => {
                    handleStartNewEncounter(e, patientId, client)
                  }
                },
                ...(urlFromPath
                  ? [{
                    id: 'igao',
                    icon: MdContentPasteGo,
                    label: igaoPages.find((ip) => ip.id === path)?.name ?? 'IGAO',
                    onClick: () => {
                      if (urlFromPath) {
                        navigate(`/patient/${client}/${patientId}/assessment/${urlFromPath}?igao-item-id=${itemId}`)
                      }
                    }
                  }]
                  : [])
              ]}
              onClose={() => ({})}
            />
          </div>
        )
      }
    }]

  if (!query?.data?.rows) {
    console.log(query)
    return null
  }

  return (
    <div style={{ padding: '0 8rem', display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '2rem' }}>
      <TableHeader tabs={query.data.tabs} />
      <Card.Card>
        <Card.Header hideBorders>
          <h2>Tasks</h2>
        </Card.Header>
        <Card.ExpandableWrapper>
          <Table.Table
            columns={columns}
            data={query.data.rows ?? []}
            filters={filters.map((f) => ({ ...f, value: currentFilters[f.name] ?? [] }))}
            queryStatus={query.status}
            fetchStatus={query.fetchStatus}
            sort={{ field: sort.field, direction: sort.dir } }
            onFilterChange={(field, values) => handleFilterChange(field, values)}
            onFiltersClear={() => ({})}
            onSortChange={(field, direction) => setSort({ dir: direction, field })}
            pagination={{ page: pagination.page, pageSize: pagination.size, totalFiltered: query.data.total }}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page: page - 1 }))}
            onPageSizeChange={(size) => setPagination({ page: 0, size })}
          />
        </Card.ExpandableWrapper>
      </Card.Card>
    </div>
  )
}

interface TableHeaderProps {
  tabs: Response['tabs']
}

export function TableHeader ({ tabs }: TableHeaderProps): JSX.Element | null {
  if (!tabs) {
    return null
  }

  return (
    <div className={styles.rowGap}>
      <div className={styles.headerTab}>
        <span className={styles.title}>My Issues</span>
        <span className={styles.value}>{tabs.my_issues}</span>
      </div>

      <div className={styles.mainRow}>
        <div className={styles.rowGap}>
          <div className={styles.tab}>
            <span className={styles.title}>New Actions</span>
            <span className={styles.value}>{tabs.new_actions}</span>
          </div>
        </div>
        <div className={styles.rowGap}>
          <div className={styles.tab}>
            <span className={styles.title}>Open Actions</span>
            <span className={styles.value}>{tabs.open_actions}</span>
          </div>
        </div>
        <div className={styles.rowGap}>
          <div className={styles.tab}>
            <span className={styles.title}>Hold Actions</span>
            <span className={styles.value}>{tabs.hold_actions}</span>
          </div>
        </div>

        <div className={styles.rowGap}>
          <div className={styles.tab}>
            <span className={styles.title}>Urgent Actions</span>
            <span className={styles.value}>{tabs.urgents}</span>
          </div>
        </div>
      </div>

      <div className={styles.headerTab}>
        <span className={styles.title}>Most Due Days</span>
        <span
          className={cx(styles.value, [ diffDate(tabs.most_outdated) >= 0 ? styles.blue : styles.red ])}
        >
          {Math.ceil(Math.abs(diffDate(tabs.most_outdated)))}
        </span>
      </div>
    </div>
  )
}
